import { useEffect, useMemo, useRef, useState } from 'react'
import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl'

type HeatMapPosition = {
  lat?: number | string
  lng?: number | string
  latitude?: number | string
  longitude?: number | string
  weight?: number | string
}

type SalesMapProps = {
  heatMapPositions: HeatMapPosition[]
  initialZoom?: number
  width?: string
  height?: string
}

type Position = { lat: number; lng: number; weight: number }

export const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron'

const BRAZIL_CENTER: [number, number] = [-51.9253, -14.235]
const MAX_FIT_ZOOM = 8
const STYLE_LOAD_TIMEOUT_MS = 15000

const MAP_LOCALE = {
  'AttributionControl.ToggleAttribution': 'Mostrar créditos',
  'Map.Title': 'Mapa',
  'NavigationControl.ZoomIn': 'Aproximar',
  'NavigationControl.ZoomOut': 'Afastar',
  'CooperativeGesturesHandler.WindowsHelpText':
    'Use Ctrl + rolagem para dar zoom no mapa',
  'CooperativeGesturesHandler.MacHelpText':
    'Use ⌘ + rolagem para dar zoom no mapa',
  'CooperativeGesturesHandler.MobileHelpText':
    'Use dois dedos para mover o mapa',
}

function toNumber(value: number | string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null

  const parsedValue =
    typeof value === 'number' ? value : Number(value.replace(',', '.'))

  return Number.isFinite(parsedValue) ? parsedValue : null
}

export function normalizePositions(
  heatMapPositions: HeatMapPosition[]
): Position[] {
  return heatMapPositions
    .map((position) => {
      const lat = toNumber(position.lat ?? position.latitude)
      const lng = toNumber(position.lng ?? position.longitude)
      const weight = toNumber(position.weight) ?? 1

      if (lat === null || lng === null) return null

      return { lat, lng, weight: Math.max(weight, 1) }
    })
    .filter(Boolean) as Position[]
}

export function toFeatureCollection(positions: Position[]) {
  return {
    type: 'FeatureCollection' as const,
    features: positions.map((position) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [position.lng, position.lat],
      },
      properties: {
        radius: Math.min(18, 4 + Math.sqrt(position.weight) * 2),
        opacity: Math.min(0.55, 0.18 + position.weight / 25),
      },
    })),
  }
}

export function centerOf(positions: Position[]): [number, number] {
  if (!positions.length) return BRAZIL_CENTER

  return [
    positions.reduce((total, position) => total + position.lng, 0) /
      positions.length,
    positions.reduce((total, position) => total + position.lat, 0) /
      positions.length,
  ]
}

export function boundsOf(
  positions: Position[]
): [[number, number], [number, number]] {
  const lngs = positions.map((position) => position.lng)
  const lats = positions.map((position) => position.lat)

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ]
}

function fitToPositions(map: MapLibreMap, positions: Position[]) {
  if (positions.length > 1) {
    map.fitBounds(boundsOf(positions), {
      padding: 48,
      maxZoom: MAX_FIT_ZOOM,
      duration: 0,
    })
  } else if (positions.length === 1) {
    map.jumpTo({ center: centerOf(positions) })
  }
}

export default function SalesMap({
  heatMapPositions,
  initialZoom = 4,
  width = '100%',
  height = '454px',
}: SalesMapProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  const positions = useMemo(
    () => normalizePositions(heatMapPositions),
    [heatMapPositions]
  )

  const positionsRef = useRef(positions)
  positionsRef.current = positions

  useEffect(() => {
    let map: MapLibreMap | null = null
    let cancelled = false
    let styleLoaded = false
    let loadTimeout: number | undefined

    function fail() {
      if (cancelled) return
      window.clearTimeout(loadTimeout)
      map?.remove()
      map = null
      mapRef.current = null
      setLoadFailed(true)
    }

    async function renderMap() {
      if (!mapElementRef.current) return

      try {
        const [{ default: maplibregl }] = await Promise.all([
          import('maplibre-gl'),
          import('maplibre-gl/dist/maplibre-gl.css'),
        ])

        if (cancelled || !mapElementRef.current) return

        map = new maplibregl.Map({
          container: mapElementRef.current,
          style: MAP_STYLE_URL,
          center: centerOf(positionsRef.current),
          zoom: initialZoom,
          attributionControl: {},
          cooperativeGestures: true,
          locale: MAP_LOCALE,
        })

        map.addControl(
          new maplibregl.NavigationControl({ showCompass: false }),
          'top-right'
        )
      } catch {
        fail()
        return
      }

      const loadedMap = map

      loadTimeout = window.setTimeout(() => {
        if (!styleLoaded) fail()
      }, STYLE_LOAD_TIMEOUT_MS)

      loadedMap.on('error', (event: { error?: { url?: string } }) => {
        if (!styleLoaded && event.error?.url === MAP_STYLE_URL) fail()
      })

      loadedMap.on('load', () => {
        styleLoaded = true
        window.clearTimeout(loadTimeout)

        loadedMap.addSource('sales', {
          type: 'geojson',
          data: toFeatureCollection(positionsRef.current),
        })

        loadedMap.addLayer({
          id: 'sales-points',
          type: 'circle',
          source: 'sales',
          paint: {
            'circle-color': '#dc2626',
            'circle-opacity': ['get', 'opacity'],
            'circle-stroke-color': '#dc2626',
            'circle-stroke-opacity': 0.35,
            'circle-stroke-width': 1,
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              3,
              ['*', ['get', 'radius'], 0.6],
              8,
              ['*', ['get', 'radius'], 1.6],
            ],
          },
        })

        fitToPositions(loadedMap, positionsRef.current)
        mapRef.current = loadedMap
      })
    }

    renderMap()

    return () => {
      cancelled = true
      window.clearTimeout(loadTimeout)
      mapRef.current = null
      map?.remove()
    }
  }, [initialZoom])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    map
      .getSource<GeoJSONSource>('sales')
      ?.setData(toFeatureCollection(positions))
    fitToPositions(map, positions)
  }, [positions])

  if (loadFailed) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#555',
          background: '#f5f5f5',
        }}
      >
        Mapa indisponível
      </div>
    )
  }

  return <div ref={mapElementRef} style={{ width, height }} />
}
